import { create } from 'zustand';
import adminService from '../services/admin/admin.service';
import { ADMIN_TOKEN_KEY } from '../services/admin/adminAxios';
import type { CurrentAdmin } from '../types/admin/admin.types';

interface AdminAuthState {
  admin: CurrentAdmin | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
  has: (permissionKey: string) => boolean;
  hasTemplate: (templateKey: string, action: string) => boolean;
}

export const useAdminAuthStore = create<AdminAuthState>((set, get) => ({
  admin: null,
  token: typeof window !== 'undefined' ? localStorage.getItem(ADMIN_TOKEN_KEY) : null,
  isAuthenticated: false,
  loading: false,

  login: async (username, password) => {
    const res = await adminService.login(username, password);
    localStorage.setItem(ADMIN_TOKEN_KEY, res.token);
    set({ token: res.token });
    const me = await adminService.me();
    set({ admin: me.data, isAuthenticated: true });
  },

  logout: () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    set({ admin: null, token: null, isAuthenticated: false });
  },

  // Re-hydrate the session from a stored token (called on app mount).
  refresh: async () => {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (!token) {
      set({ isAuthenticated: false, admin: null, token: null });
      return;
    }
    set({ loading: true });
    try {
      const me = await adminService.me();
      set({ admin: me.data, token, isAuthenticated: true, loading: false });
    } catch {
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      set({ admin: null, token: null, isAuthenticated: false, loading: false });
    }
  },

  has: (permissionKey) => {
    const { admin } = get();
    if (!admin) return false;
    return (
      admin.is_super_admin ||
      admin.permissions.includes('*') ||
      admin.permissions.includes(permissionKey)
    );
  },
  hasTemplate: (templateKey, action) => {
    return get().has(`${templateKey}.${action}`);
  },
}));
